import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';

import { joinIds } from '../internal/aria';
import { useMergedFieldAria } from './FormField';
import styles from './TextArea.module.css';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /** Long-form markdown writeups get the editor minimum height. */
  editor?: boolean;
  /** Show "n / max" beside the field. Requires `maxLength`. */
  showCount?: boolean;
  /** Mono for markdown source, which is what the authoring inspector edits. */
  monospace?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    invalid = false,
    editor = false,
    showCount = false,
    monospace = false,
    className,
    rows = 4,
    ...rest
  },
  ref,
) {
  const countId = useId();
  const withCount = showCount && rest.maxLength !== undefined;

  const aria = useMergedFieldAria({
    id: rest.id,
    describedBy: joinIds(rest['aria-describedby'], withCount && countId),
    invalid,
    required: rest.required,
  });

  const length = typeof rest.value === 'string' ? rest.value.length : undefined;

  return (
    <div className={styles.wrapper}>
      <textarea
        {...rest}
        {...aria}
        ref={ref}
        rows={rows}
        className={clsx(
          styles.textarea,
          editor && styles.editor,
          monospace && styles.mono,
          className,
        )}
      />
      {withCount && (
        <p className={styles.count} id={countId}>
          {length === undefined
            ? `Maximum ${String(rest.maxLength)} characters`
            : `${String(length)} / ${String(rest.maxLength)}`}
        </p>
      )}
    </div>
  );
});
